import {useEffect, useRef} from "react";
import {useSelector, useDispatch} from 'react-redux';
import {checkAuthorizationStatus, setOpLoading, setStatus} from '../features/authorizationSlice';
import {ACTION_TYPES} from "./actionTypes";
import {useEmployeesAPIs} from "../api/getEmployees";
import {BaseRequest} from "../utils/Classes/Requests/BaseRequest";
import {useToast} from '@chakra-ui/react'
import {useNavigate} from "react-router-dom";
import {useManageEmployeeStatus} from "../api/manageEmployeeStatus";
import {fetchEmployees} from '../features/employeesSlice';
import {useExchangeApi} from "../api/ExchangeAPI";
import {fetchBranches} from "../features/branchesSlice";
import useBranchAPI from "../api/branchAPI";
import {useLegalEmployeesAPIs} from "../api/LegalEmployee";
import {fetchLegalEmployees} from "../features/legalEmployeesSlice";
import BankTransferAPI from "../api/BankTransferAPI";
import useS2SAPI from "../api/S2S_API";
import OverviewAPI from "../api/OverviewAPI";
import {
    resetResponseState,
    setFlag, setIsPopup,
    setLoading,
    setResponseDescription,
    setResponseTitle,
    setResponseInfo
} from "../features/responseSlice";
import useLogout from "../api/logoutApi";
import {qrTimeoutMinutes} from "../utils/Constants";
import {setQrTimeout} from '../features/authorizationSlice';
import MerchantInformationAPI from "../api/MerchantInformationAPI";
import PaymentRequestAPI from "../api/PaymentRequestAPI";
import useCardsAPI from "../api/CardsAPI";

export default function useCheckAuthorizationStatusHandler(onCloseQrReset) {
    const {DeleteEmployee, EditEmployee, AddEmployee} = useEmployeesAPIs();
    const {ManageEmployeesStatus} = useManageEmployeeStatus();
    const {
        ManageLegalEmployeesStatus,
        EditLegalEntityEmployee,
        DeleteLegalEntityEmployee,
        AddLegalEntityEmployee,
        EditEmployeeLimitation
    } = useLegalEmployeesAPIs();
    const {updateExchangeRate} = useExchangeApi();
    const {AddBranch, EditBranch, UpdateBranchVisibility} = useBranchAPI();
    const {PushBankTransfer, PushBulkBankTransfer} = BankTransferAPI();
    const {PushS2STransfer, PushBulkS2STransfer} = useS2SAPI();
    const {InitiateExchange} = useExchangeApi();
    const {ConfirmTransferToLegal, ApproveRejectAwaitingTrx, ApproveListAwaitingTrx} = OverviewAPI();
    const {ManageAutoExchange} = MerchantInformationAPI();
    const {EditMerchantInfo} = MerchantInformationAPI();
    const {InitiatePaymentRequest, InitiateBulkPaymentRequest, ReversePayment} = PaymentRequestAPI();
    const {RequestCorporateCard, EditCorporateCard, ActivateDeactivateCorporateCard, CancelCorporateCard} = useCardsAPI();

    const controller = new AbortController();
    const timer = useRef(0);
    const {
        authorizationCode,
        status,
        qrTimeout,
        error,
        actionType,
        actionPayload
    } = useSelector((state) => state.authorization);
    const dispatch = useDispatch();
    const _isMounted = useRef(false);
    const navigate = useNavigate();
    const toast = useToast();
    const toastRef = useRef(null);
    const {logout} = useLogout();

    useEffect(() => {
        if (!_isMounted.current) {
            _isMounted.current = true;
            return;
        }
        let countDownDate = new Date();
        countDownDate.setMinutes(countDownDate.getMinutes() + qrTimeoutMinutes);
        countDownDate = countDownDate.getTime();

        if (!qrTimeout && authorizationCode && authorizationCode.length > 0) {
            timer.current = setInterval(() => {
                let now = new Date().getTime();
                let distance = countDownDate - now;

                if (distance < 0 || (!authorizationCode && authorizationCode.length === 0)) {
                    clearInterval(timer.current);
                    dispatch(setQrTimeout(true));
                    return;
                }
                dispatch(checkAuthorizationStatus({authCode: authorizationCode, controller}))
            }, 2000);
        } else {
            controller.abort();
            clearInterval(timer.current);
        }
    }, [authorizationCode]);

    const successResponse = (res) => {
        dispatch(setLoading("idle"));

        if (Number(res?.flagCode) === 11) {
            dispatch(setFlag(11));
            dispatch(setResponseInfo({
                amount: res?.amount,
                currencyFrom: res?.currencyFrom,
                currencyTo: res?.currencyTo,
            }));
            dispatch(setIsPopup(true));
            dispatch(setResponseTitle(res.Title));
            dispatch(setResponseDescription(res.Message));
            return;
        }

        if (res.isPopup) {
            dispatch(setIsPopup(true));
            dispatch(setFlag(res?.flagCode || 0));
            dispatch(setResponseTitle(res.Title));
            dispatch(setResponseDescription(res.Message));
        } else {
            dispatch(setFlag(0));
            toastRef.current = toast({
                position: "top",
                title: res.Title,
                description: res.Message,
                status: "success",
                duration: "5000",
                isClosable: "false",
            });
        }
    }

    const errorResponse = (error) => {
        if (error?.response?.status === 401) {
            logout();
            return;
        }

        if (error.response.data.isPopup) {
            if(error.response.data.PopupTitle === "LoaderError"){
                return;
            }
            if (error?.response?.status === 435 || error?.response?.status === 436) {
                dispatch(setFlag(error.response.status));
            } else {
                dispatch(setFlag(10));
            }
            dispatch(setLoading("idle"));
            dispatch(setIsPopup(true));
            dispatch(setResponseTitle(error.response.data.PopupTitle));
            dispatch(setResponseDescription(error.response.data.PopupDescription));
        } else {
            dispatch(setLoading("pending"));
        }
    }

    useEffect(() => {
        const check = async (actionType, payload) => {
            let data;
            switch (actionType) {
                case ACTION_TYPES.CHANGE_RATES:
                    data = await updateExchangeRate(payload);
                    if (data) {
                        toastRef.current = toast({
                            position: "top",
                            title: data.title,
                            description: data.message,
                            status: "success",
                            duration: "5000",
                            isClosable: "false",
                        });
                    }
                    break;

                case ACTION_TYPES.EDIT_EMPLOYEE:
                    data = await EditEmployee(payload).then((res) => {
                        successResponse(res);
                    }).catch((error) => {
                        errorResponse(error);
                    }).finally(() => {
                        dispatch(fetchEmployees());
                        dispatch(fetchBranches());
                    });
                    break;
                case ACTION_TYPES.DELETE_EMPLOYEE:
                    data = await DeleteEmployee(payload).then((res) => {
                        successResponse(res);
                    }).catch((error) => {
                        errorResponse(error);
                    }).finally(() => {
                        dispatch(fetchEmployees());
                        dispatch(fetchLegalEmployees());
                        dispatch(fetchBranches());
                    });

                    break;
                case ACTION_TYPES.ADD_EMPLOYEE || ACTION_TYPES.MIGRATE_EMPLOYEE:
                    data = await AddEmployee(payload).then((res) => {
                        successResponse(res);
                    }).catch((error) => {
                        errorResponse(error);
                    }).finally(() => {
                        dispatch(fetchEmployees());
                    });
                    break;
                case ACTION_TYPES.BLOCK_EMPLOYEE:
                    data = await ManageEmployeesStatus(payload).then((res) => {
                        successResponse(res);
                    }).catch((error) => {
                        errorResponse(error);
                    }).finally(() => {
                        dispatch(fetchEmployees());
                        dispatch(fetchLegalEmployees());
                    });

                    break;

                case ACTION_TYPES.ADD_LEGAL_EMPLOYEE:
                    data = AddLegalEntityEmployee(payload).then((res) => {
                        successResponse(res);
                    }).catch((error) => {
                        errorResponse(error);
                    }).finally(() => {
                        dispatch(fetchLegalEmployees());
                    });
                    break;

                case ACTION_TYPES.BLOCK_LEGAL_EMPLOYEE:
                    data = await ManageLegalEmployeesStatus(payload).then((res) => {
                        successResponse(res);
                    }).catch((error) => {
                        errorResponse(error);
                    }).finally(() => {
                        dispatch(fetchLegalEmployees());
                    });
                    break;
                case ACTION_TYPES.EDIT_LEGAL_EMPLOYEE:
                    data = await EditLegalEntityEmployee(payload).then((res) => {
                        successResponse(res);
                    }).catch((error) => {
                        errorResponse(error);
                    }).finally(() => {
                        dispatch(fetchLegalEmployees());
                    });
                    break;
                case ACTION_TYPES.DELETE_LEGAL_EMPLOYEE:
                    data = await DeleteLegalEntityEmployee(payload).then((res) => {
                        successResponse(res);
                    }).catch((error) => {
                        errorResponse(error);
                    }).finally(() => {
                        dispatch(fetchLegalEmployees());
                    });
                    dispatch(setStatus("COMPLETE"));
                    break;

                case ACTION_TYPES.ADD_BRANCH:
                    data = await AddBranch(payload).then((res) => {
                        successResponse(res);
                        dispatch(setStatus("COMPLETE"));
                        dispatch(fetchBranches());
                    }).catch((error) => {
                        errorResponse(error);
                    });


                    break;
                case ACTION_TYPES.EDIT_BRANCH:
                    data = await EditBranch(payload).then((res) => {
                        successResponse(res);
                        dispatch(setStatus("COMPLETE"));
                        dispatch(fetchBranches());
                        navigate("/branches");
                    }).catch((error) => {
                        errorResponse(error);
                    });

                    break;

                // case ACTION_TYPES.DELETE_BRANCH:
                //     data = await DeleteBranch(payload);
                //     navigate("/homepage/branches");
                //     dispatch(fetchBranches());
                //     break;
                case ACTION_TYPES.HIDE_BRANCH:
                    data = await UpdateBranchVisibility(payload).then((res) => {
                        successResponse(res);
                        dispatch(fetchBranches());
                    }).catch((error) => {
                        errorResponse(error);
                    });
                    break;

                case ACTION_TYPES.PUSH_BANK_TRANSFER:
                    dispatch(setLoading("pending"));
                    data = PushBankTransfer(payload).then((res) => {
                        successResponse(res);
                    }).catch((error) => {
                        errorResponse(error);
                    });
                    if (!data) dispatch(resetResponseState());
                    break;

                case ACTION_TYPES.PUSH_BULK_BANK_TRANSFER:
                    dispatch(setLoading("pending"));
                    data = PushBulkBankTransfer(payload).then((res) => {
                        successResponse(res);
                    }).catch((error) => {
                        errorResponse(error);
                    });
                    if (!data) dispatch(resetResponseState());
                    break;

                case ACTION_TYPES.PUSH_S2S_TRANSFER:
                    dispatch(setLoading("pending"));

                    data = PushS2STransfer(payload).then((res) => {
                        successResponse(res);
                    }).catch((error) => {
                        errorResponse(error);
                    });
                    if (!data) dispatch(resetResponseState());
                    break;

                case ACTION_TYPES.PUSH_BULK_S2S_TRANSFER:
                    dispatch(setLoading("pending"));

                    data = PushBulkS2STransfer(payload).then((res) => {
                        successResponse(res);
                    }).catch((error) => {
                        errorResponse(error);
                    });
                    if (!data) dispatch(resetResponseState());
                    break;

                case ACTION_TYPES.INITIATE_EXCHANGE:
                    dispatch(setLoading("pending"));
                    data = InitiateExchange(payload).then((res) => {
                        // dispatch(setLoading("idle"));
                        // if (res.data.Payload.isPopup) {
                        //     dispatch(setIsPopup(true));
                        //     dispatch(setFlag(0));
                        //     dispatch(setResponseTitle(res.data.Payload.PopupTitle));
                        //     dispatch(setResponseDescription(res.data.Payload.PopupDescription));
                        // }
                        successResponse(res);
                    }).catch((error) => {
                        // dispatch(setLoading("idle"));
                        // if (error.response.data.isPopup){
                        //     dispatch(setIsPopup(true));
                        //     dispatch(setFlag(1));
                        //     dispatch(setResponseTitle(error.response.data.PopupTitle));
                        //     dispatch(setResponseDescription(error.response.data.PopupDescription));
                        // }
                        errorResponse(error);
                    });
                    if (!data) dispatch(resetResponseState());
                    break;

                case ACTION_TYPES.CONFIRM_INITIATE_TRANSFER:
                    data = ConfirmTransferToLegal(payload).then((res) => {
                        successResponse(res);
                    }).catch((error) => {
                        errorResponse(error);
                    })

                    break;
                case ACTION_TYPES.APPROVE_TRX:
                    dispatch(setLoading("pending"));
                    data = ApproveRejectAwaitingTrx(payload).then((res) => {
                        successResponse(res);
                    }).catch((error) => {
                        errorResponse(error);
                    }).finally(() => {
                        dispatch(setStatus("Complete"));
                    })
                    break;
                case ACTION_TYPES.APPROVE_LIST_TRX:
                    dispatch(setLoading("pending"));
                    data = ApproveListAwaitingTrx(payload).then((res) => {
                        successResponse(res);
                        dispatch(setStatus("Complete"));
                    }).catch((error) => {
                        errorResponse(error);
                    });
                    break;
                case ACTION_TYPES.MANAGE_AUTO_CONVERSION:
                    data = ManageAutoExchange(payload).then((res) => {
                        dispatch(setResponseInfo({checked: res.data.Payload.IsOn}));
                        dispatch(setFlag(0));
                    }).catch((error) => {
                        errorResponse(error);
                    });
                    break;
                case ACTION_TYPES.EDIT_MERCHANT_INFO:
                    dispatch(setLoading("pending"));
                    data = await EditMerchantInfo(payload).then((res) => {
                        successResponse(res);
                        dispatch(setLoading("idle"));
                    }).catch((error) => {
                        errorResponse(error);
                    })
                    break;
                case ACTION_TYPES.EDIT_EMPLOYEE_LIMITATION:
                    dispatch(setLoading("pending"));
                    data = await EditEmployeeLimitation(payload).then((res) => {
                        successResponse(res);
                        dispatch(setLoading("idle"));
                    }).catch((error) => {
                        errorResponse(error);
                    });
                    break;
                case ACTION_TYPES.INITIATE_PAYMENT_REQUEST:
                    dispatch(setLoading("pending"));
                    data = await InitiatePaymentRequest(payload).then((res) => {
                        successResponse(res);
                    }).catch((error) => {
                        errorResponse(error);
                    });
                    break;
                case ACTION_TYPES.INITIATE_BULK_PAYMENT_REQUEST:
                    dispatch(setLoading("pending"));
                    data = await InitiateBulkPaymentRequest(payload).then((res) => {
                        successResponse(res);
                    }).catch((error) => {
                        errorResponse(error);
                    });
                    break;
                case ACTION_TYPES.REVERSE_TRANSACTION:
                    dispatch(setLoading("pending"));
                    data = await ReversePayment(payload).then((res) => {
                        successResponse(res);
                    }).catch((error) => {
                        errorResponse(error);
                    });
                case ACTION_TYPES.REQUEST_CARD:
                    dispatch(setLoading("pending"));
                    data = await RequestCorporateCard(payload).then((res) => {
                        successResponse(res);
                    }).catch((error) => {
                        errorResponse(error);
                    });
                    break;
                case ACTION_TYPES.EDIT_CARD:
                    dispatch(setLoading("pending"));
                    data = await EditCorporateCard(payload).then((res) => {
                        successResponse(res);
                    }).catch((error) => {
                        errorResponse(error);
                    });
                    break;
                case ACTION_TYPES.ACTIVATE_DEACTIVATE_CARD:
                    dispatch(setLoading("pending"));
                    data = await ActivateDeactivateCorporateCard(payload).then((res) => {
                        successResponse(res);
                    }).catch((error) => {
                        errorResponse(error);
                    });
                    break;
                case ACTION_TYPES.CANCEL_CARD:
                    dispatch(setLoading("pending"));
                    data = await CancelCorporateCard(payload).then((res) => {
                        successResponse(res);
                    }).catch((error) => {
                        errorResponse(error);
                    });
                    break;

                default:
                    break;
            }

            return data;
        }

        if (error || qrTimeout || (status && status.length > 0 && (status === "AUTHORIZED" || status === "EXPIRED" || status === "CLOSED" || status === "USED"))) {
            if (status === "AUTHORIZED") {
                const baseRequest = new BaseRequest(sessionStorage.getItem("SessionID"));
                const payload = {...baseRequest, ...actionPayload, AuthorizationCode: authorizationCode}
                check(actionType, payload);
                dispatch(setOpLoading("pending"));
            }
            onCloseQrReset();
            clearInterval(timer.current);
            return;
        }
    }, [status, actionType, actionPayload]);

}